import React from 'react';

import classNames from 'classnames';

import { useBreakpoint } from '@ast/magma/breakpoints';

import { DropDownMenu } from '@ast/magma/components/dropdownmenu/dropdownmenu';
import {
  disableBodyScroll,
  enableBodyScroll,
} from '@ast/magma/components/modal';

import { useIsAnimationsOff } from '@app/common/utils/hooks/useIsAnimationsOff/useIsAnimationsOff';

import {
  AccountsDropDownButton,
  AccountsDropDownButtonBaseProps,
} from './AccountsDropDownButton';
import { AccountsDropDownContextProvider } from './AccountsDropDownContext';
import {
  AccountsDropDownGroup,
  AccountsDropDownGroupProps,
} from './AccountsDropDownGroup';
import { AccountsDropDownHeader } from './AccountsDropDownHeader';
import { AccountsDropDownOption } from './AccountsDropDownOption';
import { AccountsDropDownOverlay } from './AccountsDropDownOverlay';
import styles from './styles.pcss';
import { AccountsDropDownItemProps } from './types';

export type AccountsDropDownProps<T> = Omit<AccountsDropDownButtonBaseProps, 'value' | 'children'> & {
  // Should be used AccountsDropDownGroup
  readonly children?: React.ReactElement<AccountsDropDownGroupProps> | React.ReactElement<AccountsDropDownGroupProps>[]
  /**
   * undefined - value not set initially
   * null - value is cleared
   */
  readonly value?: T

  readonly onChange?: (value: T | null) => void

  readonly disabled?: boolean
  // eslint-disable-next-line i18next/no-literal-string
  readonly 'data-stable-name'?: string;
};

export enum HandledDropDownStates {
  EXPANDING = 'expanding',
  COLLAPSED = 'collapsed',
  EXPANDED = 'expanded',
}

/**
 * It is not independent component.
 * Before using it should be wrapper by React.forwardref.
 * Actually, it is callback function for React.forwardRef.
 * Should not be used as common component.
 */
export function AccountsDropDown<T>(
  props: React.PropsWithChildren<AccountsDropDownProps<T>>,
  ref: React.Ref<HTMLButtonElement>,
) {
  const {
    buttonPlaceholder,
    children,
    // eslint-disable-next-line i18next/no-literal-string
    'data-stable-name': dataStableName,
    disabled,
    label,
    onChange,
    value,
  } = props;

  const isMobile = useBreakpoint().device === 'mobile';
  const [isDropDownOpen, dropDownOpen] = React.useState<boolean>(false);
  const [displayHidden, showHiddenAccounts] = React.useState<boolean>(false);
  const [hasHidden, setHasHidden] = React.useState<boolean>(false);
  const [selectedItemView, setSelectedItemView] = (
    React.useState<React.ReactElement | null>(null)
  );
  const contentRef = React.useRef<HTMLDivElement | null>(null);

  const isAnimationOff = useIsAnimationsOff();

  // DropDown menu at the moment not exports OpenEvent interface
  const onStateChanged = React.useCallback(
    ({ state: dropDownState }: any) => {
      const handledStates: string[] = Object.values(HandledDropDownStates);
      if (!handledStates.includes(dropDownState)) {
        return;
      }

      const isOpen = dropDownState === HandledDropDownStates.EXPANDING
        // in case when device animation is off, DropDown menu emits only 'EXPANDED / COLLAPSED' state
        || dropDownState === HandledDropDownStates.EXPANDED;

      const isClosed = dropDownState === HandledDropDownStates.COLLAPSED;

      dropDownOpen(
        (isOpen && true)
        || (isClosed && false),
      );
    },
    [],
  );

  const onSelect = (newvalue: T) => {
    if (onChange) onChange(newvalue);
    dropDownOpen(false);
  };

  React.useEffect(
    () => {
      if (isMobile) {
        if (isDropDownOpen) {
          // TODO: should be as part of dropdown menu
          disableBodyScroll();
        } else {
          enableBodyScroll();
        }
      }

      if (isDropDownOpen) {
        contentRef.current?.querySelector('button')?.focus();
        contentRef.current?.querySelector('.custom-scrollbar')?.scrollTo(0, 0);
      }
    },
    [isDropDownOpen],
  );

  React.useEffect(() => () => {
    if (isMobile) enableBodyScroll();
  }, []);

  React.useEffect(
    () => {
      if (!value) {
        setSelectedItemView(null);
        return;
      }

      // Workaround.
      const valueStr = Object.keys(value).join('');
      const groups = React.Children.toArray(children);
      const allAccounts = groups.reduce(
        (accounts, child) => {
          const childTyped = child as React.ReactElement<AccountsDropDownGroupProps>;
          const accountsTyped = accounts as React.ReactElement<AccountsDropDownItemProps<T>>[];

          if (childTyped.type === AccountsDropDownGroup) {
            return [...accountsTyped, ...(
              React.Children.toArray(childTyped.props.children)
                .filter((groupChild) => {
                  const typed = groupChild as React.ReactElement;
                  return typed.type !== AccountsDropDownOption;
                })
            )];
          }
          return accounts;
        },
        [],
      ) as React.ReactElement<AccountsDropDownItemProps<T>>[];

      const match = allAccounts.find(
        (item) => {
          const itemTyped = item as React.ReactElement;
          return (
            Object.keys(itemTyped.props.value).join('') === valueStr
            && value && (value as any).id === itemTyped.props.value.id
          );
        },
      );

      if (!match) {
        setSelectedItemView(null);
        if (onChange) onChange(null);
      } else {
        setSelectedItemView(match);
      }
    },
    [value, children],
  );

  React.useEffect(
    () => {
      const listener = (event: KeyboardEvent) => {
        const buttons = Array.from(contentRef.current?.querySelectorAll('button') || []);

        const keyCodes: { [k: string]: number } = {
          ArrowUp: -1,
          ArrowDown: 1,
        };

        const { currentTarget, key } = event;
        if (currentTarget && keyCodes[key]) {
          const currentButton = currentTarget as HTMLButtonElement;
          const buttonPosition = buttons.indexOf(currentButton);

          const nextPositon = ((position, all) => {
            if (position === -1) return 0;

            const next = position + keyCodes[key];
            if (next < 0) return all.length - 1;
            if (next >= all.length) return 0;

            return next;
          })(buttonPosition, buttons);

          buttons[nextPositon].focus();
          buttons[nextPositon].setAttribute('tab-index', '0');
          currentButton.setAttribute('tab-index', '-1');
        }
      };

      contentRef.current?.querySelectorAll('button')
        .forEach((button) => {
          button.addEventListener('keydown', listener);
        });

      return () => {
        contentRef.current?.querySelectorAll('button')
          .forEach((button) => {
            button.removeEventListener('keydown', listener);
          });
      };
    },
    [contentRef.current, displayHidden],
  );

  return (
    <div
      className={styles.container}
      data-stable-name={dataStableName}
    >
      { label && (
        <div
          className={styles.title}
          data-stable-name="AccountsDropDownHeaderText"
        >
          { !isDropDownOpen && label }
        </div>
      )}

      <DropDownMenu
        buttonRenderer={() => (
          <AccountsDropDownButton
            buttonPlaceholder={buttonPlaceholder}
            label={label}
          >
            {selectedItemView}
          </AccountsDropDownButton>
        )}
        className={classNames(
          styles.dropdown,
          isDropDownOpen && styles.open,
        )}
        data-stable-name="AccountsDropDownButton"
        disabled={disabled}
        isOpen={isDropDownOpen}
        onStateChange={onStateChanged}
        isAnimationDisabled={isAnimationOff}
        ref={ref}
      >
        <div
          data-stable-name="AccountsDropDownListbox"
          className={styles.contentWrapper}
          ref={contentRef}
        >
          <AccountsDropDownHeader
            displayHidden={displayHidden}
            hasHidden={hasHidden}
            onClick={() => {
              showHiddenAccounts((current) => !current);
            }}
            title={buttonPlaceholder}
          />
          <AccountsDropDownContextProvider
            value={{
              displayHidden,
              onSelect,
              setHasHidden,
              setSelectedItemView,
              value,
            }}
          >
            <div className={classNames('custom-scrollbar', styles.content)}>
              { children }
            </div>
          </AccountsDropDownContextProvider>
        </div>
      </DropDownMenu>

      { isDropDownOpen && (
        <AccountsDropDownOverlay
          onClick={() => { dropDownOpen(false); }}
        />
      ) }
    </div>
  );
}
